import { Editor } from '@tiptap/core';
import { PopoverProps } from 'antd';
import { debounce } from 'lodash';
import {
  ComponentType,
  FC,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { EditorPopover } from './popover';
import { EditorPopoverContentProps, EditorPopoverState } from './types';
import { getCoordinatesFromSelection } from '../utils/editor-utils';

interface Props {
  editor: Editor;
  content: ComponentType<EditorPopoverContentProps>;
  placement?: PopoverProps['placement'];
  canShow?: (editor: Editor) => boolean;
  positionRelativeToStartOfSelection?: boolean;
  canIgnoreOutsideClickFor?: (target: HTMLElement) => boolean;
  popoverContentContainerClassName?: string;
  getPosition?: (editor: Editor) => DOMRect;
  onHide?: () => void;
}
const defaultCanShow = (editor: Editor) => editor.isEditable;

export const SelectionActionPopover: FC<Props> = ({
  editor,
  content: Content,
  placement,
  canShow = defaultCanShow,
  canIgnoreOutsideClickFor,
  positionRelativeToStartOfSelection = true,
  popoverContentContainerClassName,
  getPosition,
  onHide,
}) => {
  const [popoverState, setPopoverState] = useState<EditorPopoverState>({
    visible: false,
  });

  const showPopover = useCallback(() => {
    const position = getPosition
      ? getPosition(editor)
      : getCoordinatesFromSelection(editor, positionRelativeToStartOfSelection);

    setPopoverState({
      visible: true,
      position,
    });
  }, [editor, positionRelativeToStartOfSelection, getPosition]);

  const hidePopover = useCallback(() => {
    setPopoverState({ visible: false });

    onHide?.();
  }, [onHide]);

  const selectionHandler = useCallback(() => {
    const { from, to } = editor.state.selection;
    const hasSelection = from !== to;
    const isInTable = editor.isActive('table');

    // Show popover if there's a text selection OR if cursor is inside a table cell
    if ((hasSelection || isInTable) && canShow(editor)) {
      showPopover();
    } else {
      hidePopover();
    }
  }, [canShow, editor, hidePopover, showPopover]);

  const debouncedSelectionHandler = useMemo(
    () => debounce(selectionHandler, 100),
    [selectionHandler],
  );

  useEffect(() => {
    return () => {
      debouncedSelectionHandler.cancel();
    };
  }, [debouncedSelectionHandler]);

  useEffect(() => {
    editor.on('selectionUpdate', debouncedSelectionHandler);

    return () => {
      editor.off('selectionUpdate', debouncedSelectionHandler);
    };
  }, [editor, debouncedSelectionHandler]);

  return (
    <EditorPopover
      editor={editor}
      content={Content}
      popoverState={popoverState}
      hidePopover={hidePopover}
      placement={placement}
      canIgnoreOutsideClickFor={canIgnoreOutsideClickFor}
      popoverContentContainerClassName={popoverContentContainerClassName}
    />
  );
};
